import React, { useState, useEffect } from 'react';
import { Admin, Resource } from 'react-admin';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { Provider } from 'react-redux';
import { createHashHistory } from 'history';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import './App.css';

import * as domainMessages from 'react-admin-import-csv/lib/i18n';
import authProvider from './authProvider';
import MyLoginPage from './Login';
import Layout from './Layout';
import DispatchPage from './Dispatch';
import customRoutes from './routes';
import englishMessages from './i18n/en';

import {
  ServiceVehicleCreate,
  ServiceVehicleEdit,
  ServiceVehicleList,
} from './tabs/service_vehicles';

import { ServiceAreaList, ServiceAreaEdit } from './tabs/service_areas';

import {
  ServiceOfferingList,
  CatalogCreate,
  CatalogEdit,
} from './tabs/service_offerings';

import {
  ServiceCustomMessageCreate,
  ServiceCustomMessageEdit,
  ServiceCustomMessageList,
} from './tabs/service_offerings/ServiceCustomMessage';

import {
  ServiceOfferingChecklistCreate,
  ServiceOfferingChecklistEdit,
  ServiceOfferingChecklistList,
} from './tabs/service_offerings/ServiceOfferingChecklist';

import {
  ServiceOfferingDescriptionCreate,
  ServiceOfferingDescriptionEdit,
  ServiceOfferingDescriptionList,
} from './tabs/service_offerings/ServiceOfferingDescription';

import { CouponCreate, CouponEdit, CouponList } from './tabs/coupons';

import {
  ClientLocationCreate,
  ClientLocationEdit,
  ClientLocationList,
} from './tabs/client_locations';

import { KeyoskCreate, KeyoskEdit, KeyoskList } from './tabs/keyosk';

import { ParkingCreate, ParkingEdit, ParkingList } from './tabs/parking';

import { PriceCreate, PriceEdit, PriceList } from './tabs/price';

import { BlurbCreate, BlurbEdit, BlurbList } from './tabs/technician_blurb';

import {
  InspectionCreate,
  InspectionEdit,
  InspectionList,
} from './tabs/inspection';

import {
  HealthCheckCreate,
  HealthCheckEdit,
  HealthCheckList,
} from './tabs/health-check';

import { ScheduledWorkList, ROShow } from './tabs/scheduled_work';

import { ShortUrlCreate, ShortUrlList } from './tabs/short_url';

import { EmployeeCreate, EmployeeEdit, EmployeeList } from './tabs/employee';

import { CustomerCreate, CustomerList, CustomerShow } from './tabs/customer';

import {
  CustomerVehicleCreate,
  CustomerVehicleEdit,
  CustomerVehicleList,
  CustomerVehicleShow,
} from './tabs/customer_vehicle';

import {
  ScheduleTypeCreate,
  ScheduleTypeEdit,
  ScheduleTypeList,
} from './tabs/schedule_type';

import dataProvider from './dataProvider';
import { LeadCreate, LeadEdit, LeadList } from './tabs/lead';

import {
  CarFaxRecordEdit,
  CarFaxRecordList,
  CarFaxRecordShow,
} from './tabs/carfax-record';
import { sendToFullStory, getLocalStorage } from './helper';
import {
  CustomerAddressCreate,
  CustomerAddressEdit,
  CustomerAddressList,
  CustomerAddressShow,
} from './tabs/customer-address';

import { QuoteList, QuoteRequestList } from './tabs/quotes';

import {
  PaymentMethodCreate,
  PaymentMethodEdit,
  PaymentMethodList,
} from './tabs/payment_method';

import ScrollToTop from './components/ScrollToTop';

import createAdminStore from './store/createAdminStore';
import {
  ConditionOptionsCreate,
  ConditionOptionsList,
} from './tabs/inspection/ConditionOptions';
import { PriceSheetList } from './tabs/price_sheet';
import {
  ServiceBayCreate,
  ServiceBayEdit,
  ServiceBayList,
} from './tabs/service_bays';
import getSSOUserDetails from './getSSOUserDetails';

const history = createHashHistory();

// Notify parent window of URL changes
function notifyParentOfUrlChange() {
  const currentPath = window.location.hash || window.location.pathname;
  const isFromS360 = window?.location.ancestorOrigins?.[0]?.includes('simple360') || window?.location.href.includes('fromS360');
  
  if (isFromS360) {
    const targetOrigin = window.location.host.includes('staging')
      ? 'https://simple360-temp-staging-branch-simpletire.vercel.app'
      : 'https://app.simple360.com';
    
    window.parent.postMessage({
      type: 'urlChange',
      path: currentPath
    }, targetOrigin);
  }
}

// Listen for hash changes
window.addEventListener('hashchange', notifyParentOfUrlChange);

// Override history methods for programmatic navigation
const originalPushState = history.pushState;
const originalReplaceState = history.replaceState;

history.pushState = function(...args) {
  originalPushState.apply(history, args);
  notifyParentOfUrlChange();
};

history.replaceState = function(...args) {
  originalReplaceState.apply(history, args);
  notifyParentOfUrlChange();
};

const App = () => {
  const [loading, setLoading] = useState(true);
  const isFromS360 = window?.location.ancestorOrigins?.[0]?.includes('simple360') || window?.location.href.includes('fromS360');
  useEffect(() => {
    const initialize = async () => {
      if (isFromS360) {
        await getSSOUserDetails();
      }
      const user = getLocalStorage('user');
      if (user) {
        sendToFullStory(user);
      }
      setLoading(false);
    };
    initialize();
  }, [isFromS360]);

  // Add messages from CSV importer
  // https://github.com/benwinding/react-admin-import-csv#translation-i18n
  const messages = { ...englishMessages, ...domainMessages.en };

  const i18nProvider = polyglotI18nProvider(() => messages, 'en', {
    allowMissing: true,
  });

  if (loading || !dataProvider) {
    return (
      <div className="loader-container">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  const store = createAdminStore({
    authProvider,
    dataProvider,
    history,
  });

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistStore(store)}>
        <Admin
          title="Zippity Admin"
          dataProvider={dataProvider}
          customRoutes={customRoutes}
          authProvider={authProvider}
          dashboard={DispatchPage}
          loginPage={MyLoginPage}
          layout={Layout}
          i18nProvider={i18nProvider}
          history={history}
        >
          <ScrollToTop />
          <Resource
            name="service-vehicle"
            list={ServiceVehicleList}
            edit={ServiceVehicleEdit}
            create={ServiceVehicleCreate}
          />
          <Resource
            name="service-bay"
            list={ServiceBayList}
            edit={ServiceBayEdit}
            create={ServiceBayCreate}
          />
          <Resource
            name="service-area"
            list={ServiceAreaList}
            edit={ServiceAreaEdit}
          />
          <Resource
            name="coupon"
            list={CouponList}
            edit={CouponEdit}
            create={CouponCreate}
          />
          <Resource name="parking-type" />
          <Resource
            name="service-offering"
            list={ServiceOfferingList}
            create={CatalogCreate}
            edit={CatalogEdit}
          />
          <Resource
            name="service-offering-checklist"
            list={ServiceOfferingChecklistList}
            create={ServiceOfferingChecklistCreate}
            edit={ServiceOfferingChecklistEdit}
          />
          <Resource
            name="service-offering-description"
            list={ServiceOfferingDescriptionList}
            create={ServiceOfferingDescriptionCreate}
            edit={ServiceOfferingDescriptionEdit}
          />
          <Resource
            name="service-custom-message"
            list={ServiceCustomMessageList}
            create={ServiceCustomMessageCreate}
            edit={ServiceCustomMessageEdit}
          />
          <Resource
            name="client-location"
            list={ClientLocationList}
            edit={ClientLocationEdit}
            create={ClientLocationCreate}
          />
          <Resource
            name="keyosk"
            list={KeyoskList}
            edit={KeyoskEdit}
            create={KeyoskCreate}
          />
          <Resource
            name="parking"
            list={ParkingList}
            edit={ParkingEdit}
            create={ParkingCreate}
          />
          <Resource
            name="price"
            list={PriceList}
            edit={PriceEdit}
            create={PriceCreate}
          />
          <Resource name="price-sheet" list={PriceSheetList} />
          <Resource
            name="technician-blurb"
            list={BlurbList}
            edit={BlurbEdit}
            create={BlurbCreate}
          />
          <Resource
            name="inspection"
            list={InspectionList}
            edit={InspectionEdit}
            create={InspectionCreate}
          />
          <Resource
            name="health-check"
            list={HealthCheckList}
            edit={HealthCheckEdit}
            create={HealthCheckCreate}
          />
          <Resource
            name="employee"
            list={EmployeeList}
            edit={EmployeeEdit}
            create={EmployeeCreate}
          />
          <Resource
            name="schedule-type"
            list={ScheduleTypeList}
            create={ScheduleTypeCreate}
            edit={ScheduleTypeEdit}
          />
          <Resource name="user" />
          <Resource name="role" />
          <Resource name="schedule" />
          <Resource name="schedule/stats" />
          <Resource name="integration" />
          <Resource name="integration-connection" />
          <Resource
            name="condition-options"
            list={ConditionOptionsList}
            create={ConditionOptionsCreate}
          />
          <Resource
            name="short-url"
            list={ShortUrlList}
            create={ShortUrlCreate}
          />
          <Resource
            name="scheduled-work"
            list={ScheduledWorkList}
            show={ROShow}
          />
          <Resource
            name="customer"
            list={CustomerList}
            show={CustomerShow}
            create={CustomerCreate}
          />
          <Resource
            name="customer-address"
            create={CustomerAddressCreate}
            list={CustomerAddressList}
            show={CustomerAddressShow}
            edit={CustomerAddressEdit}
          />
          <Resource
            name="vehicle"
            list={CustomerVehicleList}
            show={CustomerVehicleShow}
            edit={CustomerVehicleEdit}
            create={CustomerVehicleCreate}
          />
          <Resource
            name="lead"
            list={LeadList}
            edit={LeadEdit}
            create={LeadCreate}
          />
          <Resource
            name="carfax-record"
            list={CarFaxRecordList}
            show={CarFaxRecordShow}
            edit={CarFaxRecordEdit}
          />
          <Resource name="quote" list={QuoteList} />
          <Resource name="quote-request" list={QuoteRequestList} />
          <Resource
            name="payment-method"
            list={PaymentMethodList}
            edit={PaymentMethodEdit}
            create={PaymentMethodCreate}
          />
          <Resource name="recurring-schedule" />
          <Resource name="pro" />
        </Admin>
      </PersistGate>
    </Provider>
  );
};

export default App;
